import styled from 'styled-components';
import { Link } from 'gatsby';
import { darken, lighten } from 'polished';
import { AppTheme } from '../../../styles/theme';

type Props = {
  size?: 's' | 'm' | 'l';
  wide?: 's' | 'm' | 'l' | 'ml' | 'head';
  color?:
    | 'primary'
    | 'secondary'
    | 'white'
    | 'green'
    | 'purple'
    | 'pink'
    | 'yellow';
  radius?: 'square' | 'rounded';
  fill?: boolean;
  whiteSpace?: 'nowrap' | 'inherit';
  inline?: boolean;
  shadow?: keyof AppTheme['boxShadow'];
};

export const Anchor = styled(Link)
  .attrs((props) => {
    return {
      type: props.type || 'button',
    };
  })
  .withConfig({
    shouldForwardProp: (propName, defaultValidatorFn) => {
      return !['fill'].includes(propName) && defaultValidatorFn(propName);
    },
  })<Props>`
  position: relative;
  appearance: none;
  display: inline-flex;
  align-items: center;
  border: 1px solid;
  border-color: transparent;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSizes.m};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  outline: 0;
  transition-duration: ${({ theme }) => theme.transitions.normal};
  cursor: pointer;

  ${({ theme, size = 'm' }) => {
    switch (size) {
      case 's':
        return {
          fontSize: theme.fontSizes.s,
        };
      case 'm':
        return {
          fontSize: theme.fontSizes.m,
        };
      case 'l':
        return {
          fontSize: theme.fontSizes.xl,
        };
    }
  }}

  ${({ theme, wide = 'm' }) => {
    // Noto Sans JP がやや下に文字がくるので、padding-bottomを2px足す
    switch (wide) {
      case 's':
        return {
          padding: `${theme.spaces.m} ${theme.spaces.l} 10px`,
        };
      case 'm':
        return {
          padding: `${theme.spaces.m} ${theme.spaces.xl} 10px`,
        };
      case 'ml':
        return {
          padding: `${theme.spaces.m} ${theme.spaces.xxxl} 18px`,
        };
      case 'l':
        return {
          padding: `${theme.spaces.l} ${theme.spaces.xxxl} 18px`,
        };
      case 'head':
        return {
          padding: `12px ${theme.spaces.xl}`,

          ...theme.screen.sp({
            padding: '12px',
          }),
        };
    }
  }}

  ${({ theme, color = 'primary' }) => {
    switch (color) {
      case 'primary':
        return {
          borderColor: theme.borderColors.primary,
          backgroundColor: theme.backgroundColors.primary,
          color: theme.fontColors.white,
          boxShadow: theme.boxShadow.blue,
          '&:hover, &:focus': {
            boxShadow: theme.boxShadow.hover,
            transform: 'translateY(1px)',
            backgroundColor: darken(0.03, theme.backgroundColors.primary),
          },
        };
      case 'secondary':
        return {
          borderColor: theme.borderColors.white,
          backgroundColor: theme.backgroundColors.white,
          color: theme.fontColors.primary,
          boxShadow: theme.boxShadow.base,
          '&:hover, &:focus': {
            boxShadow: theme.boxShadow.hover,
            transform: 'translateY(1px)',
            backgroundColor: lighten(0.3, theme.backgroundColors.blue),
          },
        };
      case 'pink':
        return {
          borderColor: theme.borderColors.secondary,
          backgroundColor: theme.backgroundColors.secondary,
          color: theme.fontColors.white,
          boxShadow: theme.boxShadow.pink,
          '&:hover, &:focus': {
            boxShadow: theme.boxShadow.hover,
            transform: 'translateY(1px)',
            backgroundColor: darken(0.03, theme.backgroundColors.secondary),
          },
        };
      case 'white':
        return {
          borderColor: theme.borderColors.default,
          backgroundColor: theme.backgroundColors.white,
          color: theme.fontColors.default,
        };
      case 'green':
        return {
          borderColor: theme.borderColors.green,
          backgroundColor: theme.backgroundColors.green,
          color: theme.fontColors.white,
          boxShadow: theme.boxShadow.green,
          '&:hover, &:focus': {
            boxShadow: theme.boxShadow.hover,
            transform: 'translateY(1px)',
            backgroundColor: darken(0.03, theme.backgroundColors.green),
          },
        };
      case 'purple':
        return {
          borderColor: theme.borderColors.purple,
          backgroundColor: theme.backgroundColors.purple,
          color: theme.fontColors.white,
          boxShadow: theme.boxShadow.purple,
          '&:hover, &:focus': {
            boxShadow: theme.boxShadow.hover,
            transform: 'translateY(1px)',
            backgroundColor: darken(0.02, theme.backgroundColors.purple),
          },
        };
      case 'yellow':
        return {
          borderColor: theme.borderColors.yellow,
          backgroundColor: theme.backgroundColors.yellow,
          color: theme.fontColors.brown,
          boxShadow: theme.boxShadow.base,
          '&:hover, &:focus': {
            boxShadow: theme.boxShadow.hover,
            transform: 'translateY(1px)',
            backgroundColor: darken(0.05, theme.backgroundColors.yellow),
          },
        };
    }
  }}

  ${({ theme, radius = 'square' }) => {
    switch (radius) {
      case 'square':
        return {
          borderRadius: theme.radii.s,
        };
      case 'rounded':
        return {
          borderRadius: theme.radii.rounded,
        };
    }
  }}

  ${({ fill = false }) => {
    if (fill) {
      return {
        width: '100%',
      };
    }
  }}

  ${({ whiteSpace = 'inherit' }) => {
    if (whiteSpace) {
      return {
        whiteSpace: (whiteSpace = 'nowrap' ? 'nowrap' : 'inherit'),
      };
    }
  }}

  ${({ theme, inline }) => {
    if (inline) {
      return {
        paddingLeft: 0,
        border: 'none',
        textAlign: 'left',
        display: 'inline-flex',
        gap: theme.spaces.m,
        width: 'auto',
        justifyContent: 'flex-start',
        '&:hover, &:focus': {
          boxShadow: 'none',
          color: theme.fontColors.primary,
        },
      };
    }
  }}
  
  box-shadow: ${({ theme, shadow }) => shadow && theme.boxShadow[shadow]};
`;
